import {
  Badge,
  Button,
  Modal,
  Table,
  TableProps,
  Tooltip,
  notification,
} from '@pankod/refine-antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import { MdInventory2 } from 'react-icons/md';
import { IInventoryLotResponse, getLowStockInventories } from 'services/inventory';

export const LowStockFloatingWidget: React.FC = () => {
  const isAutoRefreshRunningRef = useRef(false);
  const [state, setState] = useState({
    isModalVisible: false,
    isLoading: false,
    items: [] as IInventoryLotResponse[],
    page: 1,
    limit: 10,
    total: 0,
    count: 0,
    pollRemainingSec: 60,
    isCountRefreshing: false,
  });

  const fetchLowStockItems = async (page = 1, pageSize = 10) => {
    setState((prev) => ({ ...prev, isLoading: true }));
    try {
      const response = await getLowStockInventories({ start: page, limit: pageSize });
      setState((prev) => ({
        ...prev,
        items: response.data,
        total: response.total,
        page,
        limit: pageSize,
        isModalVisible: true,
      }));
    } catch (err: any) {
      notification.error({
        message: 'Failed',
        description: err.message,
      });
    } finally {
      setState((prev) => ({ ...prev, isLoading: false }));
    }
  };

  const fetchLowStockCount = useCallback(async () => {
    setState((prev) => ({ ...prev, isCountRefreshing: true }));
    try {
      const response = await getLowStockInventories({ start: 1, limit: 1 });
      setState((prev) => ({ ...prev, count: response.total }));
    } catch (_) {
      // Silent fail for background polling.
    } finally {
      setState((prev) => ({ ...prev, isCountRefreshing: false }));
    }
  }, []);

  useEffect(() => {
    fetchLowStockCount();
    setState((prev) => ({ ...prev, pollRemainingSec: 60 }));
    const intervalId = window.setInterval(() => {
      setState((prev) => ({
        ...prev,
        pollRemainingSec: prev.pollRemainingSec > 0 ? prev.pollRemainingSec - 1 : 0,
      }));
    }, 1_000);
    return () => window.clearInterval(intervalId);
  }, [fetchLowStockCount]);

  useEffect(() => {
    if (state.pollRemainingSec !== 0) return;
    if (isAutoRefreshRunningRef.current) return;

    isAutoRefreshRunningRef.current = true;
    (async () => {
      try {
        await fetchLowStockCount();
      } finally {
        setState((prev) => ({
          ...prev,
          pollRemainingSec: 60,
          isCountRefreshing: false,
        }));
        isAutoRefreshRunningRef.current = false;
      }
    })();
  }, [fetchLowStockCount, state.pollRemainingSec]);

  const onLowStockTableChange: TableProps<IInventoryLotResponse>['onChange'] = (pagination) => {
    const nextPage = pagination.current ?? 1;
    const nextLimit = pagination.pageSize ?? 10;
    fetchLowStockItems(nextPage, nextLimit);
  };

  const pollProgress = (60 - state.pollRemainingSec) / 60;

  return (
    <>
      <style>{`
        @keyframes low-stock-ring-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>

      <div
        style={{
          position: 'fixed',
          right: 24,
          bottom: 24,
          zIndex: 999,
        }}
      >
        <Badge count={state.count} overflowCount={999}>
          <Tooltip title={`Auto refresh in ${state.pollRemainingSec}s`}>
            <div
              style={{
                width: 60,
                height: 60,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  borderRadius: '50%',
                  background: state.isCountRefreshing
                    ? 'conic-gradient(#1677ff 0deg, #8fb8ff 120deg, #d9e6ff 240deg, #1677ff 360deg)'
                    : `conic-gradient(#1677ff ${pollProgress * 360}deg, #d9e6ff 0deg)`,
                  boxShadow: '0 8px 20px rgba(0, 0, 0, 0.2)',
                  animation: state.isCountRefreshing
                    ? 'low-stock-ring-spin 0.9s linear infinite'
                    : 'none',
                }}
              />
              <Button
                type="primary"
                shape="circle"
                icon={<MdInventory2 size={22} />}
                loading={state.isLoading}
                onClick={() => fetchLowStockItems(1, state.limit)}
                style={{
                  position: 'relative',
                  zIndex: 1,
                  width: 52,
                  height: 52,
                  border: '2px solid #fff',
                }}
              />
            </div>
          </Tooltip>
        </Badge>
      </div>

      <Modal
        title="Low stock products"
        visible={state.isModalVisible}
        onCancel={() => setState((prev) => ({ ...prev, isModalVisible: false }))}
        footer={null}
        width={900}
      >
        <Table<IInventoryLotResponse>
          rowKey={(record) => `${record.productVariant.id}-${record.warehouseName ?? 'unknown'}`}
          dataSource={state.items}
          loading={state.isLoading}
          onChange={onLowStockTableChange}
          pagination={{
            current: state.page,
            pageSize: state.limit,
            total: state.total,
            showSizeChanger: true,
          }}
          size="small"
        >
          <Table.Column<IInventoryLotResponse> title="SKU" dataIndex={['productVariant', 'sku']} />
          <Table.Column<IInventoryLotResponse>
            title="Name"
            dataIndex={['productVariant', 'name']}
          />
          <Table.Column<IInventoryLotResponse>
            title="Warehouse"
            render={(_, record) => record.warehouseName ?? '-'}
          />
          <Table.Column<IInventoryLotResponse>
            title="Units"
            render={(_, record) => <span style={{ color: '#cf1322' }}>{record.quantity}</span>}
          />
          <Table.Column<IInventoryLotResponse>
            title="Min Unit"
            render={(_, record) => record.minUnit}
          />
        </Table>
      </Modal>
    </>
  );
};
